import { tokens } from '@mercai/clever'
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  TooltipItem,
} from 'chart.js'
import { Radar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import type {
  RadarChartProps,
  RadarChartDataPointProps,
} from './radar-chart.type'

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
)

export const RadarChart = ({ data, className }: RadarChartProps) => {
  const { t } = useTranslation('screenAiCenter')

  const { finalScoring } = data || {}
  const scoring = finalScoring?.scoring

  const isOldStructure =
    scoring && 'products' in scoring && scoring.products !== undefined

  const radarDataOld: RadarChartDataPointProps[] = [
    {
      axis: t('conversationShowRadarChartProducts'),
      value:
        scoring && 'products' in scoring ? scoring.products?.score || 0 : 0,
      maxValue:
        scoring && 'products' in scoring ? scoring.products?.maxScore || 0 : 0,
      fullMark: 2,
    },
    {
      axis: t('conversationShowRadarChartProductUsage'),
      value:
        scoring && 'productUsage' in scoring
          ? scoring.productUsage?.score || 0
          : 0,
      maxValue:
        scoring && 'productUsage' in scoring
          ? scoring.productUsage?.maxScore || 0
          : 0,
      fullMark: 1.5,
    },
    {
      axis: t('conversationShowRadarChartMonthlyVolume'),
      value:
        scoring && 'monthlyVolume' in scoring
          ? scoring.monthlyVolume?.score || 0
          : 0,
      maxValue:
        scoring && 'monthlyVolume' in scoring
          ? scoring.monthlyVolume?.maxScore || 0
          : 0,
      fullMark: 2,
    },
    {
      axis: t('conversationShowRadarChartPurchaseFrequency'),
      value: scoring?.purchaseFrequency?.score || 0,
      maxValue: scoring?.purchaseFrequency?.maxScore || 0,
      fullMark: 1,
    },
    {
      axis: t('conversationShowRadarChartAcceptsCounterTypes'),
      value:
        scoring && 'acceptsCounterTypes' in scoring
          ? scoring.acceptsCounterTypes?.score || 0
          : 0,
      maxValue:
        scoring && 'acceptsCounterTypes' in scoring
          ? scoring.acceptsCounterTypes?.maxScore || 0
          : 0,
      fullMark: 2,
    },
    {
      axis: t('conversationShowRadarChartTargetPrice'),
      value:
        scoring && 'targetPrice' in scoring
          ? scoring.targetPrice?.score || 0
          : 0,
      maxValue:
        scoring && 'targetPrice' in scoring
          ? scoring.targetPrice?.maxScore || 0
          : 0,
      fullMark: 0.5,
    },
    {
      axis: t('conversationShowRadarChartLocation'),
      value: scoring?.location?.score || 0,
      maxValue: scoring?.location?.maxScore || 0,
      fullMark: 1,
    },
  ]

  const radarDataNew: RadarChartDataPointProps[] = [
    {
      axis: t('conversationShowRadarChartLocation'),
      value: scoring?.location?.score || 0,
      maxValue: scoring?.location?.maxScore || 0,
      fullMark: scoring?.location?.maxScore || 0,
    },
    {
      axis: t('conversationShowRadarChartQuantityMOQ'),
      value:
        scoring && 'quantityMOQ' in scoring
          ? scoring.quantityMOQ?.score || 0
          : 0,
      maxValue:
        scoring && 'quantityMOQ' in scoring
          ? scoring.quantityMOQ?.maxScore || 0
          : 0,
      fullMark:
        scoring && 'quantityMOQ' in scoring
          ? scoring.quantityMOQ?.maxScore || 0
          : 0,
    },
    {
      axis: t('conversationShowRadarChartBusinessContext'),
      value:
        scoring && 'businessContext' in scoring
          ? scoring.businessContext?.score || 0
          : 0,
      maxValue:
        scoring && 'businessContext' in scoring
          ? scoring.businessContext?.maxScore || 0
          : 0,
      fullMark:
        scoring && 'businessContext' in scoring
          ? scoring.businessContext?.maxScore || 0
          : 0,
    },
    {
      axis: t('conversationShowRadarChartPurchaseFrequency'),
      value: scoring?.purchaseFrequency?.score || 0,
      maxValue: scoring?.purchaseFrequency?.maxScore || 0,
      fullMark: scoring?.purchaseFrequency?.maxScore || 0,
    },
    {
      axis: t('conversationShowRadarChartProductAvailability'),
      value:
        scoring && 'productAvailability' in scoring
          ? scoring.productAvailability?.score || 0
          : 0,
      maxValue:
        scoring && 'productAvailability' in scoring
          ? scoring.productAvailability?.maxScore || 0
          : 0,
      fullMark:
        scoring && 'productAvailability' in scoring
          ? scoring.productAvailability?.maxScore || 0
          : 0,
    },
  ]

  const radarData = isOldStructure ? radarDataOld : radarDataNew

  const chartData = {
    labels: radarData.map((item) => item.axis),
    datasets: [
      {
        label: t('conversationShowRadarChartScore'),
        data: radarData.map((item) => (item.value / item.fullMark) * 2),
        backgroundColor: tokens.colors.primary100,
        borderColor: tokens.colors.primary600,
        borderWidth: 2,
        pointBackgroundColor: tokens.colors.primary600,
        pointBorderColor: tokens.colors.gray100,
        pointHoverBackgroundColor: tokens.colors.gray100,
        pointHoverBorderColor: tokens.colors.primary600,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 2,
        ticks: {
          stepSize: 0.5,
        },
        grid: {
          color: tokens.colors.gray200,
        },
        angleLines: {
          color: tokens.colors.gray200,
        },
        pointLabels: {
          font: {
            size: 12,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<'radar'>) {
            const index = context.dataIndex
            const item = radarData[index]
            return `${item.axis}: ${item.value}/${item.maxValue}`
          },
        },
      },
    },
  }

  return (
    <div className={className}>
      <Radar data={chartData} options={chartOptions} />
    </div>
  )
}
